import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = () => axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});

const ID_TYPES = [
 { id: 'Drivers Licence', icon: '🪪', label: "Driver's Licence", sub: 'Most common · AU or international', popular: true },
 { id: 'Passport', icon: '📘', label: 'Passport', sub: 'Australian or international passport', popular: true },
 { id: 'Medicare Card', icon: '💚', label: 'Medicare Card', sub: 'Green card · AU residents only' },
 { id: 'Photo ID Card', icon: '🆔', label: 'Photo ID Card', sub: 'State-issued proof of age card' },
 { id: 'Bank Statement', icon: '🏦', label: 'Bank Statement', sub: 'AU bank statement with address · recent' },
 { id: 'Right to Work', icon: '✅', label: 'Right to Work (Visa)', sub: 'Visa grant letter, VEVO check, or work permit' },
];

const STEPS = ['Choose IDs', 'Upload', 'Selfie', 'Submit'];

export default function IdentityVerificationPage({ user }) {
 const http = api();
 const [status, setStatus] = useState(null);
 const [step, setStep] = useState(0);
 const [selectedDocs, setSelectedDocs] = useState([]);
 const [uploads, setUploads] = useState({});
 const [selfieUploaded, setSelfieUploaded] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [submitted, setSubmitted] = useState(false);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 http.get('/identity/status').then(r => { setStatus(r.data); setLoading(false); }).catch(() => setLoading(false));
 }, []);

 const toggleDoc = (id) => {
 setSelectedDocs(prev => prev.includes(id) ? prev.filter(d => d !== id) : prev.length < 2 ? [...prev, id] : prev);
 };

 const handleSubmit = async () => {
 setSubmitting(true);
 try {
 await http.post('/identity/start', { doc_type_1: selectedDocs[0], doc_type_2: selectedDocs[1] });
 setSubmitted(true);
 setStatus({ status: 'pending', verified: false });
 } catch (err) { alert(err.response?.data?.error || 'Submission failed'); }
 setSubmitting(false);
 };

 if (loading) return <div className="min-h-screen bg-neutral-50 flex items-center justify-center"><div className="text-neutral-400">Loading...</div></div>;

 // Already verified
 if (status?.verified || status?.status === 'verified') {
 return (
 <div className="min-h-screen bg-neutral-50">
 <div className="max-w-lg mx-auto px-4 py-12">
 <div className="text-center mb-8">
 <img src="/logo.svg" alt="ShiftMatch" className="w-12 h-12 mx-auto mb-4" />
 </div>
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 text-center">
 <div className="w-20 h-20 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
 <span className="text-4xl">️</span>
 </div>
 <h1 className="text-2xl font-bold text-neutral-900 mb-2">Identity Verified ✓</h1>
 <p className="text-neutral-500 mb-6">Your identity has been confirmed. You can now apply to all shifts on ShiftMatch.</p>
 <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 text-left space-y-2 mb-6">
 <div className="flex items-center gap-2 text-sm text-brand-700"><span>✓</span><span>Document 1: {status.doc_type_1 || 'Verified'}</span></div>
 <div className="flex items-center gap-2 text-sm text-brand-700"><span>✓</span><span>Document 2: {status.doc_type_2 || 'Verified'}</span></div>
 <div className="flex items-center gap-2 text-sm text-brand-700"><span>✓</span><span>Selfie / Liveness check passed</span></div>
 {status.verified_at && <div className="text-xs text-brand-600 mt-1">Verified {new Date(status.verified_at).toLocaleDateString('en-AU')}</div>}
 </div>
 <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500/10 border border-brand-500/30 rounded-full text-brand-700 text-sm font-semibold">
 ️ Verified Worker Badge Active
 </div>
 </div>
 </div>
 </div>
 );
 }

 // Pending
 if (status?.status === 'pending' && !submitted) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 max-w-md w-full text-center">
 <div className="text-5xl mb-4">⏳</div>
 <h2 className="text-xl font-bold text-neutral-900 mb-2">Verification In Progress</h2>
 <p className="text-neutral-500 text-sm">Your documents are being reviewed by our AI. This usually takes a few minutes. We'll notify you when it's done.</p>
 <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">You can continue browsing shifts while we verify your identity.</div>
 </div>
 </div>
 );
 }

 if (submitted) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 max-w-md w-full text-center">
 <div className="text-5xl mb-4"></div>
 <h2 className="text-xl font-bold text-neutral-900 mb-2">Verification Submitted!</h2>
 <p className="text-neutral-500 text-sm mb-4">We're reviewing your documents. Usually approved within minutes.</p>
 <div className="space-y-2 text-sm text-brand-700 text-left bg-brand-50 border border-brand-200 rounded-xl p-4">
 <div>✓ Document 1: {selectedDocs[0]}</div>
 <div>✓ Document 2: {selectedDocs[1]}</div>
 <div>✓ Selfie captured</div>
 </div>
 <a href="/worker" className="mt-6 block w-full py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition text-center">Back to Dashboard →</a>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-neutral-50">
 <div className="max-w-lg mx-auto px-4 py-8">
 {/* Header */}
 <div className="text-center mb-8">
 <img src="/logo.svg" alt="ShiftMatch" className="w-10 h-10 mx-auto mb-3" />
 <h1 className="text-2xl font-bold text-neutral-900">Verify Your Identity</h1>
 <p className="text-neutral-500 mt-1 text-sm">Verified workers get hired <span className="text-brand-600 font-semibold">3× faster</span>. Takes 2 minutes.</p>
 </div>

 {/* Step indicator */}
 <div className="flex items-center justify-between mb-8">
 {STEPS.map((s, i) => (
 <React.Fragment key={s}>
 <div className="flex flex-col items-center">
 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition ${i <= step ? 'bg-brand-500 text-white' : 'bg-neutral-200 text-neutral-400'}`}>{i < step ? '✓' : i + 1}</div>
 <span className={`text-xs mt-1 ${i <= step ? 'text-brand-600 font-medium' : 'text-neutral-400'}`}>{s}</span>
 </div>
 {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-brand-400' : 'bg-neutral-200'}`} />}
 </React.Fragment>
 ))}
 </div>

 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-6">
 {/* Step 0: Choose IDs */}
 {step === 0 && (
 <div>
 <h2 className="text-lg font-bold text-neutral-900 mb-1">Choose 2 forms of ID</h2>
 <p className="text-sm text-neutral-500 mb-4">Select exactly 2 documents to verify your identity.</p>
 <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
  <strong>🌏 No Medicare?</strong><br/>
  Foreign workers can use Passport + Bank Statement, or Passport + Right to Work (Visa) documents.
 </div>
 <div className="space-y-3 mb-6">
 {ID_TYPES.map(doc => {
 const selected = selectedDocs.includes(doc.id);
 const disabled = !selected && selectedDocs.length >= 2;
 return (
 <button key={doc.id} onClick={() => !disabled && toggleDoc(doc.id)} disabled={disabled}
 className={`w-full p-4 rounded-xl border-2 text-left transition flex items-center gap-4 ${selected ? 'border-brand-500 bg-brand-50' : disabled ? 'border-neutral-100 bg-neutral-50 opacity-40 cursor-not-allowed' : 'border-neutral-200 bg-white hover:border-brand-300'}`}>
 <span className="text-2xl">{doc.icon}</span>
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <span className="font-semibold text-neutral-900">{doc.label}</span>
 {doc.popular && <span className="text-xs px-2 py-0.5 bg-brand-100 text-brand-700 rounded-full">Popular</span>}
 </div>
 <div className="text-xs text-neutral-500 mt-0.5">{doc.sub}</div>
 </div>
 <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition ${selected ? 'bg-brand-500 border-brand-500 text-white' : 'border-neutral-200'}`}>
 {selected && '✓'}
 </div>
 </button>
 );
 })}
 </div>
 <button onClick={() => setStep(1)} disabled={selectedDocs.length < 2}
 className="w-full py-3.5 bg-brand-500 hover:bg-brand-600 disabled:bg-neutral-200 disabled:text-neutral-400 text-white font-bold rounded-xl transition">
 {selectedDocs.length < 2 ? `Select ${2 - selectedDocs.length} more document${2 - selectedDocs.length !== 1 ? 's' : ''}` : 'Continue →'}
 </button>
 </div>
 )}

 {/* Step 1: Upload documents */}
 {step === 1 && (
 <div>
 <h2 className="text-lg font-bold text-neutral-900 mb-1">Upload your documents</h2>
 <p className="text-sm text-neutral-500 mb-4">Clear photos, all 4 corners visible, no glare.</p>
 <div className="space-y-4 mb-6">
 {selectedDocs.map((docType, idx) => (
 <div key={docType}>
 <label className="block text-sm font-semibold text-neutral-700 mb-2">{idx + 1}. {docType}</label>
 <label className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${uploads[docType] ? 'border-brand-400 bg-brand-50' : 'border-neutral-200 hover:border-brand-400 hover:bg-brand-50/30'}`}>
 <input type="file" accept="image/*" className="hidden" onChange={(e) => {
 if (e.target.files[0]) setUploads(prev => ({ ...prev, [docType]: e.target.files[0].name }));
 }} />
 {uploads[docType] ? (
 <div className="text-brand-600"><div className="text-2xl mb-1">✓</div><div className="text-sm font-medium">{uploads[docType]}</div></div>
 ) : (
 <div className="text-neutral-400"><div className="text-3xl mb-2"></div><div className="text-sm">Tap to upload {docType}</div><div className="text-xs mt-1">JPG, PNG — max 10MB</div></div>
 )}
 </label>
 </div>
 ))}
 </div>
 <div className="flex gap-3">
 <button onClick={() => setStep(0)} className="flex-1 py-3 border border-neutral-200 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-50 transition">← Back</button>
 <button onClick={() => setStep(2)} disabled={Object.keys(uploads).length < selectedDocs.length}
 className="flex-1 py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-neutral-200 text-white disabled:text-neutral-400 font-bold rounded-xl transition">Continue →</button>
 </div>
 </div>
 )}

 {/* Step 2: Selfie */}
 {step === 2 && (
 <div>
 <h2 className="text-lg font-bold text-neutral-900 mb-1">Take a selfie</h2>
 <p className="text-sm text-neutral-500 mb-4">Our AI checks that you match your ID photo.</p>
 <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
 <div className="flex items-center gap-2 text-blue-700 text-sm font-medium mb-1"> AI Liveness Detection</div>
 <p className="text-xs text-blue-600">Powered by ISO 30107-3 biometric AI — the same standard used by Australian banks. Detects photo spoofing and deepfakes.</p>
 </div>
 <div className="bg-neutral-100 rounded-2xl p-8 text-center mb-4">
 <div className="w-24 h-24 rounded-full bg-neutral-200 flex items-center justify-center mx-auto mb-3 text-5xl"></div>
 <p className="text-sm text-neutral-600 mb-1">Good lighting · Look at camera · No sunglasses</p>
 </div>
 <label className={`block border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition mb-6 ${selfieUploaded ? 'border-brand-400 bg-brand-50' : 'border-neutral-200 hover:border-brand-400'}`}>
 <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => { if (e.target.files[0]) setSelfieUploaded(true); }} />
 {selfieUploaded ? <div className="text-brand-600 font-medium">✓ Selfie captured</div> : <div className="text-neutral-500 text-sm"> Take selfie or upload photo</div>}
 </label>
 <div className="flex gap-3">
 <button onClick={() => setStep(1)} className="flex-1 py-3 border border-neutral-200 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-50 transition">← Back</button>
 <button onClick={() => setStep(3)} disabled={!selfieUploaded}
 className="flex-1 py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-neutral-200 text-white disabled:text-neutral-400 font-bold rounded-xl transition">Continue →</button>
 </div>
 </div>
 )}

 {/* Step 3: Review & Submit */}
 {step === 3 && (
 <div>
 <h2 className="text-lg font-bold text-neutral-900 mb-4">Review & submit</h2>
 <div className="space-y-3 mb-6">
 {selectedDocs.map((doc, i) => (
 <div key={doc} className="flex items-center gap-3 p-3 bg-brand-50 border border-brand-200 rounded-xl">
 <span className="text-brand-500 text-lg">✓</span>
 <div><p className="text-sm font-semibold text-neutral-900">Document {i+1}: {doc}</p><p className="text-xs text-neutral-500">{uploads[doc]}</p></div>
 </div>
 ))}
 <div className="flex items-center gap-3 p-3 bg-brand-50 border border-brand-200 rounded-xl">
 <span className="text-brand-500 text-lg">✓</span>
 <p className="text-sm font-semibold text-neutral-900">Selfie captured — AI liveness check ready</p>
 </div>
 </div>
 <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-xl mb-6">
 <p className="text-xs text-neutral-500"> Your documents are encrypted and never shared with employers. Only your verified status is visible.</p>
 </div>
 <div className="flex gap-3">
 <button onClick={() => setStep(2)} className="flex-1 py-3 border border-neutral-200 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-50 transition">← Back</button>
 <button onClick={handleSubmit} disabled={submitting}
 className="flex-1 py-3.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-70">
 {submitting ? 'Submitting...' : 'Submit Verification ✓'}
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Trust footer */}
 <p className="text-center text-xs text-neutral-400 mt-6">
 Encrypted · AI-powered · Same standard as Australian banks
 </p>
 </div>
 </div>
 );
}
